import React, { useState, useEffect, useRef } from 'react';
import { Settings as SettingsIcon, Save, AlertTriangle, Upload, Database, Users, Clock, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  CompanySettings,
  TariffSettings,
  ServiceSettings,
  PortSettings,
  MotorLaunchSettings,
  PortCallSettings,
  AnchorageEmailSettings,
  BackupSettings,
  FlagSettings,
  UserActivitySettings,
  AgencyTariffSettings,
  AllTariffsSettings
} from '../components/settings';

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('company');
  
  // Form data state
  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    tax_number: '',
    tax_office: '',
    logo_url: '',
    main_logo: '',
    sidebar_logo: '',
    default_currency: 'USD',
    default_tax_rate: 0,
    invoice_number_format: 'INV-{year}-{number:04d}',
    proforma_number_format: 'PF-{year}-{number:04d}',
    proforma_use_custom_format: false,
    proforma_number_prefix: 'ROPS',
    proforma_number_year: new Date().getFullYear(),
    proforma_number_start: 1,
    document_footer: '',
    exchange_rate_url: 'https://www.tcmb.gov.tr/kurlar/today.xml',
    exchange_rate_fallback_url: 'https://www.turkiye.gov.tr/doviz-kurlari',
    overtime_fee_try: 4650,
    hssgm_commercial: 17.27,
    hssgm_cabotage_small: 13.73,
    hssgm_cabotage_large: 31.00,
    hssgm_private_yacht: 6.05,
    hssgm_tourist_ship: 6.05,
    hssgm_research_ship: 6.05,
    light_dues_foreign_commercial_0_800: 0.22176,
    light_dues_foreign_commercial_800_plus: 0.11088,
    light_dues_foreign_passenger_0_800: 0.16896,
    light_dues_foreign_passenger_800_plus: 0.08448,
    light_dues_turkish_0_800: 0.1241856,
    light_dues_turkish_800_plus: 0.0620928,
    light_dues_turkish_cabotage_0_800: 0.03528,
    light_dues_turkish_cabotage_800_plus: 0.01764,
    dto_port_service_turkish: 2800.0,
    dto_port_service_foreign: 4900.0,
    dto_freight_container_single: 700.0,
    dto_freight_container_combined: 1050.0,
    dto_turkish_tiers: [],
    dto_foreign_tiers: [],
    service_items: [],
    service_categories: [], // Kullanıcı tanımlı kategoriler
    service_suppliers: [], // Kullanıcı tanımlı tedarikçiler
    ports_by_country: {},
    port_call_types: [],
    motor_launch_rates: [],
    operational_checklist_items: [],
    // Demirsahası Mail Ayarları - IMAP (Gelen)
    anchorage_imap_server: '',
    anchorage_imap_port: 993,
    anchorage_imap_email: '',
    anchorage_imap_password: '',
    anchorage_sender_filter: 'alerts@shippingexplorer.net',
    anchorage_auto_sync: true,
    // Demirsahası Mail Ayarları - SMTP (Giden)
    anchorage_smtp_server: '',
    anchorage_smtp_port: 465,
    anchorage_smtp_user: '',
    anchorage_smtp_password: '',
    anchorage_sender_email: '',
    // Hizmet Eşleştirmeleri (Tarife Hesaplamaları için)
    agency_fee_service_name: '',
    hssgm_service_name: '',
    light_dues_service_name: '',
    dto_service_name: '',
    dto_freight_service_name: '',
    overtime_service_name: '',
    transit_visa_service_name: '',
    port_authority_ordino_overtime_service_name: '',
    port_authority_lcb_overtime_service_name: '',
    port_authority_lcb_c_code_service_name: '',
    port_authority_anchoring_service_name: '',
    anchoring_free_days: 3
  });

  // Logo upload refs and states
  const mainLogoInputRef = useRef(null);
  const sidebarLogoInputRef = useRef(null);
  const [uploadingMainLogo, setUploadingMainLogo] = useState(false);
  const [uploadingSidebarLogo, setUploadingSidebarLogo] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setFormData(prev => ({
        ...prev,
        ...response.data
      }));
    } catch (error) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/settings', formData);
      toast.success('Ayarlar kaydedildi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ayarlar kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const setUploading = type === 'main' ? setUploadingMainLogo : setUploadingSidebarLogo;
    setUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        try {
          const endpoint = type === 'main' ? '/upload-logo-login' : '/upload-logo-sidebar';
          const response = await api.post(endpoint, { image_data: base64Data });
          
          if (type === 'main') {
            setFormData(prev => ({ ...prev, main_logo: response.data.url }));
          } else {
            setFormData(prev => ({ ...prev, sidebar_logo: response.data.url }));
          }
          
          toast.success('Logo başarıyla yüklendi');
        } catch (error) {
          toast.error('Logo yüklenemedi');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Logo yüklenemedi');
      setUploading(false);
    }
  };

  // Admin olmayan kullanıcılar için kısıtlı tab'lar
  const adminOnlyTabs = ['company', 'backup', 'useractivity'];
  
  const tabs = [
    // Admin için tüm tab'lar, diğer kullanıcılar için sadece belirli tab'lar
    ...(user?.role === 'admin' ? [{ id: 'company', label: 'Şirket & Belge', color: '#3b82f6' }] : []),
    { id: 'alltariffs', label: 'Tarifeler', color: '#f59e0b' },
    { id: 'services', label: 'Hizmet Kalemleri', color: '#8b5cf6' },
    { id: 'ports', label: 'Limanlar', color: '#06b6d4' },
    { id: 'flags', label: 'Gemi Bayrakları', color: '#ec4899' },
    { id: 'portcall', label: 'Liman Çağrısı', color: '#059669' },
    { id: 'anchorageemail', label: 'Demirsahası Mail', color: '#0ea5e9' },
    ...(user?.role === 'admin' ? [{ id: 'backup', label: 'Yedekleme', color: '#7c3aed' }] : []),
    ...(user?.role === 'admin' ? [{ id: 'useractivity', label: 'Kullanıcı Aktivitesi', color: '#ef4444' }] : [])
  ];

  // Eğer aktif tab admin-only ise ve kullanıcı admin değilse, ilk izin verilen tab'a git
  useEffect(() => {
    if (adminOnlyTabs.includes(activeTab) && user?.role !== 'admin') {
      setActiveTab('alltariffs');
    }
  }, [activeTab, user?.role]);

  if (loading) {
    return (
      <div className="content">
        <div style={{ textAlign: 'center', padding: '3rem' }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title" data-testid="settings-title">
          <SettingsIcon size={24} style={{ marginRight: '0.5rem' }} />
          Sistem Ayarları
        </h1>
        <div className="topbar-actions">
          {activeTab !== 'alltariffs' && activeTab !== 'backup' && activeTab !== 'useractivity' && (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="btn btn-primary"
              data-testid="save-settings-btn"
            >
              <Save size={18} />
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '2rem', maxWidth: '1200px' }}>
        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '2rem', 
          borderBottom: '2px solid #e2e8f0',
          flexWrap: 'wrap'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{
                padding: '0.75rem 1.25rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? `3px solid ${tab.color}` : '3px solid transparent',
                color: activeTab === tab.id ? tab.color : '#64748b',
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginBottom: '-2px',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Company Settings Tab */}
          {activeTab === 'company' && (
            <>
              {/* Logo Management */}
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div className="card-body">
                  <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>
                    Logo Yönetimi
                  </h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Giriş Ekranı Logosu</label>
                      <div style={{ 
                        border: '2px dashed #e2e8f0', 
                        borderRadius: '8px', 
                        padding: '1.5rem', 
                        textAlign: 'center',
                        backgroundColor: '#f8fafc'
                      }}>
                        {formData.main_logo ? (
                          <img 
                            src={formData.main_logo} 
                            alt="Main Logo" 
                            style={{ maxHeight: '80px', maxWidth: '100%', marginBottom: '1rem' }}
                          />
                        ) : (
                          <div style={{ marginBottom: '1rem', color: '#64748b' }}>
                            <Upload size={32} />
                            <p style={{ marginTop: '0.5rem' }}>Logo yüklenmedi</p>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={mainLogoInputRef}
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'main')}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          onClick={() => mainLogoInputRef.current?.click()}
                          disabled={uploadingMainLogo}
                          className="btn btn-secondary"
                        >
                          {uploadingMainLogo ? 'Yükleniyor...' : 'Logo Yükle'}
                        </button>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Sidebar Logosu</label>
                      <div style={{ 
                        border: '2px dashed #e2e8f0', 
                        borderRadius: '8px', 
                        padding: '1.5rem', 
                        textAlign: 'center',
                        backgroundColor: '#f8fafc'
                      }}>
                        {formData.sidebar_logo ? (
                          <img 
                            src={formData.sidebar_logo} 
                            alt="Sidebar Logo" 
                            style={{ maxHeight: '80px', maxWidth: '100%', marginBottom: '1rem' }}
                          />
                        ) : (
                          <div style={{ marginBottom: '1rem', color: '#64748b' }}>
                            <Upload size={32} />
                            <p style={{ marginTop: '0.5rem' }}>Logo yüklenmedi</p>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={sidebarLogoInputRef}
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(e, 'sidebar')}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          onClick={() => sidebarLogoInputRef.current?.click()}
                          disabled={uploadingSidebarLogo}
                          className="btn btn-secondary"
                        >
                          {uploadingSidebarLogo ? 'Yükleniyor...' : 'Logo Yükle'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <CompanySettings formData={formData} handleChange={handleChange} />
            </>
          )}

          {/* All Tariffs Settings Tab - Birleşik Tarifeler */}
          {activeTab === 'alltariffs' && (
            <AllTariffsSettings formData={formData} handleChange={handleChange} />
          )}

          {/* Service Settings Tab */}
          {activeTab === 'services' && (
            <ServiceSettings formData={formData} handleChange={handleChange} />
          )}

          {/* Port Settings Tab */}
          {activeTab === 'ports' && (
            <PortSettings formData={formData} handleChange={handleChange} />
          )}

          {/* Flag Settings Tab */}
          {activeTab === 'flags' && (
            <div className="card">
              <div className="card-body">
                <FlagSettings formData={formData} setFormData={setFormData} />
              </div>
            </div>
          )}

          {/* Port Call Settings Tab */}
          {activeTab === 'portcall' && (
            <PortCallSettings formData={formData} handleChange={handleChange} />
          )}

          {/* Anchorage Email Settings Tab */}
          {activeTab === 'anchorageemail' && (
            <AnchorageEmailSettings formData={formData} handleChange={handleChange} user={user} />
          )}

          {/* Backup Tab */}
          {activeTab === 'backup' && (
            <BackupSettings />
          )}

          {/* User Activity Tab (Admin Only) */}
          {activeTab === 'useractivity' && user?.role === 'admin' && (
            <UserActivitySettings />
          )}

          {/* Save Button (except for backup, user activity, and alltariffs) */}
          {activeTab !== 'backup' && activeTab !== 'useractivity' && activeTab !== 'alltariffs' && (
            <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                data-testid="save-btn"
              >
                <Save size={18} />
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Settings;
